import { type Moment } from "moment/moment"
import moment from "moment"
import { normalizeDecimal } from "@/helper/number-helper"

/**
 * Mark a Moment as time-only so that convertMomentToDateStrings serializes it
 * as HH:mm:ss instead of a full datetime. Use this when programmatically
 * creating Moments that represent Django TimeField values.
 */
export function markAsTimeOnly(m: Moment): Moment {
  ;(m as any).__timeOnly = true
  return m
}

/**
 * Create a Moment tagged as time-only for Django TimeField serialization.
 * The resulting Moment will be serialized as HH:mm:ss by DataHelper.
 */
export function createTimeMoment(input?: Moment): Moment {
  const m = input ? moment(input) : moment()
  markAsTimeOnly(m)
  return m
}

/**
 * Returns the next weekday after the given date, skipping weekends.
 */
export function getNextWeekday(moment: Moment) {
  const nextWeekday = moment.clone().add(1, "day").isoWeekday()
  let daysOffset = 0
  // Check for Saturday and adjust to Monday
  if (nextWeekday === 6) {
    daysOffset += 2
  }
  // Check for Sunday and adjust to Monday
  else if (nextWeekday === 7) {
    daysOffset += 1
  }

  return moment.clone().add(1 + daysOffset, "days")
}

/**
 * Datepicker values are kept as date-only Moments with `_f === "YYYY-MM-DD"`.
 * Those should use weekday-level availability only; real datetimes, including 00:00,
 * should still perform a time-window check.
 */
export function isDateOnly(date: Moment): boolean {
  return (date as any)._f === "YYYY-MM-DD"
}

/**
 * Validates and adjusts the start and end times of an event to ensure logical consistency.
 * This function modifies the input moments to ensure that the start and end times are
 * on the correct day as specified by the date. If the end time is earlier than the start time,
 * the end time is set to the next day to maintain chronological order.
 * NOTE: This function modifies the input moments in place.
 *
 * @param date - The date the event is supposed to happen. If undefined, a default date is used.
 * @param timeStart - The starting time of the event. If undefined, no changes are made.
 * @param timeEnd - The ending time of the event. If undefined, no changes are made.
 * @returns A tuple containing the potentially modified start and end moments.
 */
export function normalizeDateTimes(
  date?: Moment | null,
  timeStart?: Moment | null,
  timeEnd?: Moment | null
): [Moment | null | undefined, Moment | null | undefined] {
  if (date) {
    // If a specific date is provided, set both timeStart and timeEnd to this date.
    if (timeStart) {
      combineDateWithTime(date, timeStart)
    }
    if (timeEnd) {
      combineDateWithTime(date, timeEnd)
    }
  } else {
    // If no date is provided, set to default date (January 1, 1970).
    const defaultDate = moment("1970-01-01")
    timeStart?.date(defaultDate.date())
    timeEnd?.date(defaultDate.date())
  }

  if (timeStart && timeEnd) {
    // If both timeStart and timeEnd are defined, calculate their minutes from midnight.
    const timeStartMin = timeStart.minutes() + timeStart.hours() * 60
    const timeEndMin = timeEnd.minutes() + timeEnd.hours() * 60

    if (timeStartMin && timeEndMin && timeStartMin > timeEndMin) {
      // If timeEnd is earlier than timeStart, adjust timeEnd to the next day.
      timeEnd
        .set({
          year: timeStart.year(),
          month: timeStart.month(),
          date: timeStart.date()
        })
        .add(1, "day")
    }
  }

  // Return the potentially modified start and end times.
  return [timeStart, timeEnd]
}

/**
 * Returns whether two Moments represent the same clock time.
 * Nullish values are considered equal only when both are nullish.
 */
export function timesAreSame(a: Moment | null | undefined, b: Moment | null | undefined) {
  return (
    (!a && !b) || // Both null/undefined
    (a?.hours() === b?.hours() && // Hours are the same
      a?.minutes() === b?.minutes()) // Minutes are the same
  )
}

/**
 * Returns whether two Moments represent the exact same instant.
 * Nullish values are considered equal only when both are nullish.
 */
export function momentsAreSame(a: Moment | null | undefined, b: Moment | null | undefined) {
  if (!a && !b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  return a.isSame(b)
}

/**
 * Copies the calendar date from `date` into `time`.
 * This helper mutates and returns the `time` Moment.
 */
function combineDateWithTime(date: Moment, time: Moment) {
  // Set the year, month, and day from the date object to the time object
  time.year(date.year())
  time.month(date.month())
  time.date(date.date())

  return time
}

/**
 * Converts minutes to a decimal hours string with two decimal places.
 * Invalid input falls back to `"0.00"`.
 */
export function minutesToHours(minutes?: number) {
  if (typeof minutes !== "number" || isNaN(minutes)) {
    return "0.00"
  }
  return normalizeDecimal(minutes / 60, { minDecimalPlaces: 2, maxDecimalPlaces: 2 })
}

/**
 * Combines a date and time Moment into a single Moment.
 * Returns null if either argument is null/undefined.
 */
export function combineDateAndTime(
  date: Moment | null | undefined,
  time: Moment | null | undefined
): Moment | null {
  if (!date || !time) {
    return null
  }
  return modifyTime(date, time)
}

/**
 * Copies the hour, minute, and second from sourceMoment into targetMoment.
 * This function does not modify the input moments in place, but returns
 * a new moment object.
 *
 * @param {object} targetMoment - The moment object to update.
 * @param {object} sourceMoment - The moment object to copy from.
 * @returns {object} The updated targetMoment.
 */
export function modifyTime(targetMoment: Moment, sourceMoment: Moment) {
  // Build a fresh Moment from an ISO string so that the internal _f format
  // is never "YYYY-MM-DD".  convertMomentToDateStrings relies on _f to decide
  // whether to serialize as date-only or full datetime; cloning a date-only
  // Moment would preserve _f === "YYYY-MM-DD" and silently strip the time.
  return moment(
    targetMoment
      .clone()
      .set({
        hour: sourceMoment.hour(),
        minute: sourceMoment.minute(),
        second: sourceMoment.second()
      })
      .format("YYYY-MM-DDTHH:mm:ss.SSS")
  )
}

/**
 * Returns whether the given Moment is exactly at midnight.
 */
export function isMidnight(time: Moment | null | undefined): boolean {
  return !!time && time.hour() === 0 && time.minute() === 0
}

/**
 * Returns the date-only portion of a datetime as the start of that day.
 */
export function getDatePortion(datetime: Moment | null | undefined): Moment | null {
  if (!datetime) {
    return null
  }
  return datetime.clone().startOf("day")
}

/**
 * Replaces the date portion of `current` while preserving its time.
 * If `current` is missing, the returned value is the new date at midnight.
 */
export function setDatePortion(
  current: Moment | null | undefined,
  value: Moment | null
): Moment | null {
  if (!value) {
    return null
  }
  if (current) {
    return modifyTime(value, current)
  }
  return value.clone().startOf("day")
}

/**
 * Returns the time-bearing Moment unless it is midnight, which is treated as "no time".
 */
export function getTimePortion(datetime: Moment | null | undefined): Moment | null {
  if (!datetime || isMidnight(datetime)) {
    return null
  }
  return datetime
}

/**
 * Replaces the time portion of `current` while preserving its date.
 * Clearing the time resets the value to midnight on the same date.
 */
export function setTimePortion(
  current: Moment | null | undefined,
  value: Moment | null
): Moment | null {
  if (!current) {
    return current ?? null
  }
  if (!value) {
    return current.clone().startOf("day")
  }
  return modifyTime(current, value)
}

/**
 * Returns whether the given time range overlaps the given calendar day.
 */
export function overlapsDate(
  timeStart: Moment | null | undefined,
  timeEnd: Moment | null | undefined,
  date: Moment
): boolean {
  const dayStart = date.clone().startOf("day")
  const dayEnd = dayStart.clone().add(1, "day")

  if (timeStart?.isSameOrAfter(dayEnd)) {
    return false
  }

  return !timeEnd?.isSameOrBefore(dayStart)
}

/**
 * Formats a date for display, optionally including the year.
 */
export function formatDateText(mom: Moment, includeYear = false) {
  const format = includeYear ? "dddd D MMMM YYYY" : "dddd D MMMM"
  return mom.format(format)
}

/**
 * Returns a generic, date-agnostic label describing how many calendar days `target`
 * falls after (or before) `base`. Unlike {@link formatDateText}, this does not expose a
 * concrete date, which makes it suitable for contexts without a real date (e.g. templates
 * built on a fixed placeholder date). Same day yields an empty string.
 */
export function relativeDayLabel(base: Moment, target: Moment): string {
  const dayDiff = target.clone().startOf("day").diff(base.clone().startOf("day"), "days")
  if (dayDiff === 0) {
    return ""
  }
  if (dayDiff === 1) {
    return "volgende dag"
  }
  if (dayDiff === -1) {
    return "vorige dag"
  }
  return dayDiff > 0 ? `+${dayDiff} dagen` : `${dayDiff} dagen`
}

/**
 * Returns a human-friendly date label for today, tomorrow, and yesterday,
 * falling back to the regular formatted date text otherwise.
 */
export function smartDateString(mom: Moment) {
  if (mom.isSame(moment(), "day")) {
    return "vandaag"
  } else if (mom.isSame(moment().add(1, "day"), "day")) {
    return "morgen"
  } else if (mom.isSame(moment().subtract(1, "day"), "day")) {
    return "gisteren"
  } else {
    return formatDateText(mom)
  }
}
